/**
 * Map menu-import API errors to user-facing i18n keys (menuImporters namespace).
 * Server text is never returned as-is for DoorDash, Uber Eats or Ordering.co.
 */

const ORDERING_REJECTION_MARK = "{'error': True";

const UNREACHABLE_DETAIL =
  /max retries exceeded|connection refused|name or service not known|client error|server error/i;

export const parseOrderingRejection = (detail) => {
  const text = String(detail || "");
  if (!text.includes(ORDERING_REJECTION_MARK)) return null;
  const start = text.indexOf("'result':");
  if (start === -1) return null;
  const reasons = [...text.slice(start).matchAll(/'([^']*)'/g)]
    .map((match) => match[1])
    .filter((value) => value && value !== "result");
  if (!reasons.length) return null;
  return { count: reasons.length, reason: reasons[0] };
};

const orderingRejectionMessage = (detail, t) => {
  const parsed = parseOrderingRejection(detail);
  if (!parsed) return null;
  if (parsed.count === 1) {
    return t("errors.orderingRejectedOne", { reason: parsed.reason });
  }
  return t("errors.orderingRejected", {
    count: parsed.count,
    reason: parsed.reason,
  });
};

const mapGloriaFoodError = (detail, status, t) => {
  if (status === 403) return t("errors.gloriafood.providerBlocked");
  if (status === 504) return t("errors.gloriafood.timeout");
  if (status === 422) {
    if (/menu items|menu categories|empty/i.test(detail || "")) {
      return t("errors.gloriafood.menuEmpty");
    }
    return t("errors.gloriafood.invalidMenu");
  }
  if (status === 400) return t("errors.gloriafood.invalidKey");
  if (status === 401) return t("errors.gloriafood.unauthorized");
  if (status === 502 || status === 503)
    return t("errors.gloriafood.unavailable");
  return t("errors.gloriafood.generic");
};

const isRetryableStatus = (status) => status === 503 || status === 504;

/**
 * @returns {{ message: string, retryable: boolean }}
 */
export const classifyMenuImportError = (source, detail, status, t) => {
  const text = String(detail || "");
  if (text.includes("MENU_IMPORT_RATE_LIMITED")) {
    const applied = Number((text.match(/applied=(\d+)/) || [])[1] || 0);
    const retryAfter = (text.match(/retry_after=(\d+)/) || [])[1];
    if (applied > 0 && retryAfter) {
      return {
        message: t("errors.rateLimitedApplied", {
          applied,
          retry_after: retryAfter,
        }),
        retryable: true,
      };
    }
    if (retryAfter) {
      return {
        message: t("errors.rateLimited", { retry_after: retryAfter }),
        retryable: true,
      };
    }
    return { message: t("errors.rateLimitedLater"), retryable: true };
  }

  if (source === "gloriafood") {
    return {
      message: mapGloriaFoodError(detail, status, t),
      retryable: isRetryableStatus(status),
    };
  }

  const rejection = orderingRejectionMessage(detail, t);
  if (rejection) {
    return { message: rejection, retryable: false };
  }

  if (status === 504 || /timed out/i.test(text)) {
    return { message: t("errors.timeout"), retryable: true };
  }

  if (source === "ordering" && /ORDERING_SOURCE_UNAUTHORIZED/.test(text)) {
    return { message: t("errors.sourceUnauthorized"), retryable: false };
  }

  if (source === "ordering" && status === 404) {
    return { message: t("errors.sourceNotFound"), retryable: false };
  }

  if (source === "ordering" && UNREACHABLE_DETAIL.test(text)) {
    return {
      message: t("errors.sourceUnreachable"),
      retryable: isRetryableStatus(status),
    };
  }

  if (status === 401) {
    return { message: t("errors.unauthorized"), retryable: false };
  }
  if (status === 400) {
    return { message: t("errors.invalidInput"), retryable: false };
  }
  if (status === 422) {
    return { message: t("errors.menuEmpty"), retryable: false };
  }
  if (status === 503) {
    return { message: t("errors.providerUnavailable"), retryable: true };
  }

  return { message: t("status.error"), retryable: false };
};

export const mapMenuImportError = (source, detail, status, t) =>
  classifyMenuImportError(source, detail, status, t).message;
